import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Calendar, Image, Settings, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    contentCount: 0,
    courseCount: 0,
    galleryCount: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [content, courses, gallery] = await Promise.all([
          axios.get(`${API}/content`),
          axios.get(`${API}/courses`),
          axios.get(`${API}/gallery`)
        ]);
        setStats({
          contentCount: content.data.length,
          courseCount: courses.data.length,
          galleryCount: gallery.data.length
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, []);

  const dashboardCards = [
    {
      title: 'Seiteninhalte',
      description: 'Texte und Bilder auf den Seiten bearbeiten',
      icon: FileText,
      link: '/admin/content',
      stat: `${stats.contentCount} Inhalte`,
      color: 'bg-blue-500'
    },
    {
      title: 'Kurse & Termine',
      description: 'Kursangebote und Termine verwalten',
      icon: Calendar,
      link: '/admin/courses',
      stat: `${stats.courseCount} Kurse`,
      color: 'bg-[#F4A261]'
    },
    {
      title: 'Galerie',
      description: 'Praxisbilder verwalten',
      icon: Image,
      link: '/admin/gallery',
      stat: `${stats.galleryCount} Bilder`,
      color: 'bg-[#2A9D8F]'
    },
    {
      title: 'Einstellungen',
      description: 'Kontaktdaten und allgemeine Einstellungen',
      icon: Settings,
      link: '/admin/settings',
      stat: 'Konfiguration',
      color: 'bg-[#264653]'
    }
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#264653] font-['Nunito']">
          Dashboard
        </h1>
        <p className="text-[#5F6F75] mt-2">
          Willkommen im Admin-Bereich. Hier können Sie alle Inhalte Ihrer Website verwalten.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {dashboardCards.map((card) => {
          const Icon = card.icon;
          return (
            <Link key={card.link} to={card.link}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full" data-testid={`dashboard-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardHeader className="pb-2">
                  <div className={`w-12 h-12 rounded-lg ${card.color} flex items-center justify-center mb-4`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-lg text-[#264653] font-['Nunito']">
                    {card.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-[#5F6F75] mb-2">{card.description}</p>
                  <p className="text-sm font-medium text-[#F4A261]">{card.stat}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Quick Links */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg text-[#264653] font-['Nunito']">
            Schnellzugriff
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <a 
              href="/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ExternalLink className="h-5 w-5 text-[#F4A261]" />
              <div>
                <p className="font-medium text-[#264653]">Website ansehen</p>
                <p className="text-sm text-[#5F6F75]">Öffentliche Seite öffnen</p>
              </div>
            </a>
            <Link 
              to="/admin/courses"
              className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Calendar className="h-5 w-5 text-[#F4A261]" />
              <div>
                <p className="font-medium text-[#264653]">Neuen Kurs anlegen</p>
                <p className="text-sm text-[#5F6F75]">Kurstermine hinzufügen</p>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
